'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useQuery } from '@tanstack/react-query';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable, TextError } from '@/components/common/Error';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { useVaultV2WithdrawLogic } from '@/components/VaultPage/VaultV2/hooks/useVaultV2WithdrawLogic';
import ForceDeallocationOrderCallout from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/ForceDeallocationOrderCallout';
import { useInKindRedemptionEntry } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2InKindRedemptionDialog';
import { VaultV2WithdrawReviewDialog } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog';
import { getParsedValue } from '@/helpers/formatters';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import { orpc } from '@/lib/orpc/client';
import { toVaultV2PositionManagerData } from '../toVaultV2PositionManagerData';
import VaultV2TransactionInfoCard from '../VaultV2TransactionInfoCard';
import VaultV2PositionManagerWithdrawDialogLoading from './VaultV2PositionManagerWithdrawDialogLoading';
interface VaultV2PositionManagerWithdrawDialogContentProps {
  vaultAddress: Address;
  chainId: ChainId;
}
const VaultV2PositionManagerWithdrawDialogContent = t0 => {
  const $ = _c(67);
  const {
    vaultAddress,
    chainId
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== chainId || $[1] !== vaultAddress) {
    t1 = orpc.vaultV2.positionManager.queryOptions({
      input: {
        chainId,
        address: vaultAddress
      }
    });
    $[0] = chainId;
    $[1] = vaultAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: positionManager,
    isPending: isQueryLoading,
    error: queryError
  } = useQuery(t1);
  let t2;
  let vaultData;
  if ($[3] !== chainId || $[4] !== positionManager) {
    vaultData = positionManager ? toVaultV2PositionManagerData(positionManager, chainId) : undefined;
    t2 = getParsedValue(vaultData?.asset, parseApiAsset);
    $[3] = chainId;
    $[4] = positionManager;
    $[5] = t2;
    $[6] = vaultData;
  } else {
    t2 = $[5];
    vaultData = $[6];
  }
  const vaultAsset = t2;
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultBundlesV1", vaultAsset?.address, chainId);
  let t3;
  if ($[7] !== vaultAddress || $[8] !== vaultData) {
    t3 = {
      vaultData,
      vaultAddress
    };
    $[7] = vaultAddress;
    $[8] = vaultData;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const logic = useVaultV2WithdrawLogic(t3);
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    inputMax,
    finalPosition,
    isLoading,
    userVaultAssets,
    summaryFeeAssets,
    summaryForceDeallocation,
    summaryInKindRedemption,
    showForceDeallocationOrderCallout,
    showInKindRedemptionEntry,
    error,
    resetInput,
    refetchPosition,
    refetchVaultData
  } = logic;
  let t4;
  if ($[10] !== refetchPosition || $[11] !== refetchVaultData || $[12] !== resetInput) {
    t4 = () => {
      resetInput();
      refetchPosition();
      refetchVaultData();
    };
    $[10] = refetchPosition;
    $[11] = refetchVaultData;
    $[12] = resetInput;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const onTxSuccess = t4;
  let t5;
  if ($[14] !== inputAmount || $[15] !== isMax || $[16] !== onTxSuccess || $[17] !== toggleDialog || $[18] !== userVaultAssets || $[19] !== vaultAddress || $[20] !== vaultData) {
    t5 = () => {
      if (!isDefined(userVaultAssets)) {
        return;
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="494px" />}><VaultV2WithdrawReviewDialog vaultData={vaultData} vaultAddress={vaultAddress} inputAmount={inputAmount} isMax={isMax} onTxSuccess={onTxSuccess} entryPoint="position_manager" recapSource="positionManager" /></SectionErrorBoundary>, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[14] = inputAmount;
    $[15] = isMax;
    $[16] = onTxSuccess;
    $[17] = toggleDialog;
    $[18] = userVaultAssets;
    $[19] = vaultAddress;
    $[20] = vaultData;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const openReviewDialog = t5;
  let t6;
  if ($[22] !== chainId || $[23] !== logic || $[24] !== onTxSuccess || $[25] !== openReviewDialog || $[26] !== vaultAddress || $[27] !== vaultAsset) {
    t6 = {
      source: "position_manager",
      chainId,
      vaultAddress,
      vaultAsset,
      logic,
      onReview: openReviewDialog,
      dialogOptions: POSITION_MANAGER_DIALOG_OPTIONS,
      onTxSuccess
    };
    $[22] = chainId;
    $[23] = logic;
    $[24] = onTxSuccess;
    $[25] = openReviewDialog;
    $[26] = vaultAddress;
    $[27] = vaultAsset;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const {
    liquidityCallout,
    actionButtonProps
  } = useInKindRedemptionEntry(t6);
  if (isQueryLoading) {
    let t7;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <VaultV2PositionManagerWithdrawDialogLoading />;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    return t7;
  }
  if (queryError || !vaultData || !vaultAsset) {
    let t7;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ErrorServiceUnavailable minHeight="400px" />;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    return t7;
  }
  const t7 = `Withdraw ${vaultAsset.symbol}`;
  const t8 = inputMax?.value;
  let t9;
  if ($[31] !== inputAmount || $[32] !== isLoading || $[33] !== isMax || $[34] !== onMaxClick || $[35] !== setInputAmount || $[36] !== t7 || $[37] !== t8 || $[38] !== vaultAsset) {
    t9 = <CompactAssetInput header={t7} token={vaultAsset} amount={inputAmount} onChange={setInputAmount} maxValue={t8} onMaxClick={onMaxClick} isMax={isMax} loading={isLoading} />;
    $[31] = inputAmount;
    $[32] = isLoading;
    $[33] = isMax;
    $[34] = onMaxClick;
    $[35] = setInputAmount;
    $[36] = t7;
    $[37] = t8;
    $[38] = vaultAsset;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== error.userVaultPositionError) {
    t10 = error.userVaultPositionError && <div className="flex justify-end"><TextError message="Error fetching vault position." messageTooltip={error.userVaultPositionError.message} /></div>;
    $[40] = error.userVaultPositionError;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t9) {
    t11 = <div className="gap-2xs flex flex-col">{t9}{t10}</div>;
    $[42] = t10;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== showForceDeallocationOrderCallout) {
    t12 = showForceDeallocationOrderCallout && <ForceDeallocationOrderCallout />;
    $[45] = showForceDeallocationOrderCallout;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== finalPosition || $[48] !== summaryFeeAssets || $[49] !== summaryForceDeallocation || $[50] !== summaryInKindRedemption || $[51] !== userVaultAssets || $[52] !== vaultData) {
    t13 = <VaultV2TransactionInfoCard vaultData={vaultData} initialPosition={userVaultAssets} finalPosition={finalPosition} feeAssets={summaryFeeAssets} forceDeallocation={summaryForceDeallocation} inKindRedemption={summaryInKindRedemption} />;
    $[47] = finalPosition;
    $[48] = summaryFeeAssets;
    $[49] = summaryForceDeallocation;
    $[50] = summaryInKindRedemption;
    $[51] = userVaultAssets;
    $[52] = vaultData;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== bundleCanTransfer || $[55] !== showInKindRedemptionEntry) {
    t14 = !showInKindRedemptionEntry && !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[54] = bundleCanTransfer;
    $[55] = showInKindRedemptionEntry;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  const t15 = actionButtonProps.isFinalizeDisabled || !showInKindRedemptionEntry && !bundleCanTransfer;
  let t16;
  if ($[57] !== actionButtonProps || $[58] !== t15) {
    t16 = <ActionButtons {...actionButtonProps} isFinalizeDisabled={t15} />;
    $[57] = actionButtonProps;
    $[58] = t15;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== liquidityCallout || $[61] !== t11 || $[62] !== t12 || $[63] !== t13 || $[64] !== t14 || $[65] !== t16) {
    t17 = <>{t11}{liquidityCallout}{t12}{t13}{t14}{t16}</>;
    $[60] = liquidityCallout;
    $[61] = t11;
    $[62] = t12;
    $[63] = t13;
    $[64] = t14;
    $[65] = t16;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  return t17;
};
export default VaultV2PositionManagerWithdrawDialogContent;